import { Button, cn, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { WidgetType } from '@/api/rest/resources/types/widget';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
} from '@/components/NCExplainers/Explainer';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { getHexFromString } from '@/utils/getHexFromString';

type ChartCategory = {
  kingdom: string;
  origin?: string;
  group?: string;
  value: number;
  unit?: string;
};
type InvasiveSpeciesControlProps = {
  data: PlotReportFactElement[];
  kingdomLabel: string;
  groupLabel: string;
  kingdoms: string[];
  categoryShades?: Record<string, string>;
};

export const InvasiveSpeciesControl = (props: InvasiveSpeciesControlProps) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.labels.heading')}>
      <InvasiveSpeciesControlCard {...props} />
    </PlotCardWrapper>
  );
};

const InvasiveSpeciesControlCard = ({
  data,
  kingdomLabel = 'kingdom',
  groupLabel = 'group',
  kingdoms = ['flora', 'fauna'],
  categoryShades = { flora: '#5dbd26', fauna: '#c0880e' },
}: InvasiveSpeciesControlProps) => {
  const { t } = useTranslation();
  if (!Array.isArray(kingdoms) || typeof kingdoms[0] !== 'string') {
    throw new Error(`kingdoms array must contain at least one string value, ${kingdoms} received`);
  }

  const [activeKingdomType, setActiveKingdomType] = useState<string>(kingdoms[0]);

  const categoryToGroupList = getCategoryToGroupList({
    type: activeKingdomType,
    data,
    kingdomLabel,
    groupLabel,
  });

  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <InvasiveSpeciesControlExplainer />,
  };

  const TITLE = t(`shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.labels.heading`);

  const headerProps = {
    title: TITLE,
    children: (
      <Stack className='flex flex-row gap-2 self-center px-2 w-fit'>
        {kingdoms.map((type) => (
          <Button
            size='small'
            key={type}
            variant='outline'
            className={cn(activeKingdomType === type ? 'bg-primary-12 text-white' : 'bg-white text-text-secondary')}
            onClick={() => setActiveKingdomType(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </Button>
        ))}
      </Stack>
    ),
  };
  const footerProps = {
    logos: ['tnfd', 'gri', 'sbtn', 'cdp', 'csrd', 'cbd', 'npi', 'sai'],
  };

  const statProps = {
    children: <Stats categoryToGroupList={categoryToGroupList} />,
  };

  const graphHeaderProps = {
    heading: TITLE,
    popover: {
      title: t('shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.explainer.invasiveSpeciesControl.title'),
      body: t('shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.explainer.invasiveSpeciesControl.body'),
    },
    className: 'w-full px-6 sm:px-0 justify-start',
  };

  const graphProps = {
    children: <GraphTile categoryToGroupList={categoryToGroupList} categoryShades={categoryShades} />,
    graphHeaderProps,
    className: 'h-full justify-start items-center',
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      statProps={statProps}
      graphProps={graphProps}
    />
  );
};

const Stats = ({ categoryToGroupList }: { categoryToGroupList: ReturnType<typeof getCategoryToGroupList> }) => {
  const { t } = useTranslation();
  const categoriesMap = categoryToGroupList.reduce<
    Record<string, { value: number; label: string; category: string; unit?: string }>
  >((acc, item) => {
    const categoryKey = getCategoryLabel(item);

    let category = acc[`${categoryKey}`];
    if (!category) {
      category = { value: 0, label: categoryKey, category: categoryKey, unit: item?.unit };
      acc[`${categoryKey}`] = category;
    }
    category.value += item.value;
    return acc;
  }, {});

  return (
    <Stack className='gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
      <ul className='list-inside list-disc space-y-1 ml-6 w-full'>
        {Object.values(categoriesMap).map(({ label, value, category, unit }) => (
          <li key={category}>
            <span className='typography-body1Semibold'>{label}: </span>
            <span>{`${getDisplayNumber(value, window.navigator.language)}${
              unit ? t(`global.ui.unit.${unit as UnitEnum}`) : ''
            }`}</span>
          </li>
        ))}
      </ul>
    </Stack>
  );
};
const getCategoryToGroupList = ({
  type,
  data,
  kingdomLabel,
  groupLabel,
}: {
  type: string;
  data: PlotReportFactElement[];
  kingdomLabel: string;
  groupLabel?: string;
}): ChartCategory[] => {
  const dataByType = data.filter((item) => item.labels?.find((label) => label.name === kingdomLabel)?.value === type);

  return dataByType.flatMap((fact) => {
    if (!fact?.value || fact?.labels?.length === 0) {
      return [];
    }

    const origin = fact.labels?.find((label) => label.name === 'origin')?.value as string | undefined;
    const group = fact.labels?.find((label) => label.name === groupLabel)?.value as string | undefined;

    if (!origin && !group) {
      return [];
    }

    return {
      kingdom: type,
      origin,
      group,
      value: fact.value as number,
      unit: fact.unit as string | undefined,
    };
  });
};

const getCategoryLabel = (category: ChartCategory): string => {
  const { origin, group, kingdom } = category;
  const originAsTitle = origin ? origin.charAt(0).toUpperCase() + origin.slice(1).replaceAll('_', ' ') : undefined;
  const label = group ?? kingdom;

  if (originAsTitle) {
    return `${originAsTitle} ${label}`;
  }

  return label.charAt(0).toUpperCase() + label.slice(1).replaceAll('_', ' ');
};

const GraphTile = ({
  categoryToGroupList,
  categoryShades,
}: {
  categoryShades?: Record<string, string>;
  categoryToGroupList: ReturnType<typeof getCategoryToGroupList>;
}) => {
  const categoriesMap = categoryToGroupList.reduce<Record<string, { value: number; key: string }>>((acc, item) => {
    const categoryKey = getCategoryLabel(item);

    let category = acc[`${categoryKey}`];
    if (!category) {
      category = { value: 0, key: categoryKey };
      acc[`${categoryKey}`] = category;
    }
    category.value += item.value;
    return acc;
  }, {});

  const categoriesArray = Object.entries(categoriesMap).map(([key, { value, key: label }]) => ({
    category: key,
    label,
    value,
  }));

  const chartData = categoriesArray.map((category) => ({
    category: category.category,
    label: category.label,
    name: category.label,
    value: category.value,
    color: categoryShades?.[`${category.label}`] ?? getHexFromString(category.label),
  }));

  const legendItems = chartData.map((category) => ({
    label: category.label,
    color: category.color,
  }));

  const widgetConfiguration = {
    name: 'invasive_species_control_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'invasive-species-control-graph-chart',
      data: chartData,
      chartConfig: [
        {
          shape: ChartShape.pie,
          id: 'invasive_species_control_graph_pie',
          key: 'invasive_species_control_graph_pie',
          dataKey: 'value',
          unit: '%',
          innerRadius: 0,
          outerRadius: 108,
          startAngle: 90,
          endAngle: 450,
          paddingAngle: 0,
          cx: '50%',
          cy: '50%',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (value: any) => {
            return `${getDisplayNumber(value.value, window.navigator.language)}%`;
          },
          labelLine: false,
        },
      ],
      legendItems,
    },
  };

  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};

const InvasiveSpeciesControlExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger data-testid='invasive-species-control-explainer'>
        {t('global.howDoWeMeasureThis')}
      </ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy
              body={t(`shared.ncaDetail.details.floraFauna.invasiveSpeciesControl.explainer.howDoWeMeasureThis.body`)}
            />
          </ExplainerBody>
          {/* TODO: MVP-9692 update methodology link to correct page when available */}
          {/* <MethodologyLink to={''} /> */}
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
